import { GRAVITY, PITCH_MAX } from './types.js';

// CONVENTION (Codex iter 2 plan MAJOR fix):
// - Three.js right-handed Y-up.
// - Camera at origin with no rotation looks at -Z = "forward into the scene".
// - yaw is rotation around +Y (right-hand rule). +yaw turns the view to
//   the LEFT when looking down from above (toward -X for yaw = π/2).
// - forwardInput = (W ? 1 : 0) - (S ? 1 : 0)   // +1 = forward, -1 = backward
// - strafeInput  = (D ? 1 : 0) - (A ? 1 : 0)   // +1 = right, -1 = left
//
// Derivation:
//   forwardVec(yaw) = (-sin yaw, 0, -cos yaw)
//   rightVec(yaw)   = ( cos yaw, 0, -sin yaw)   // forward rotated -90° around Y
//   worldDx = forward * forwardVec.x + strafe * rightVec.x
//           = strafe*cos - forward*sin
//   worldDz = forward * forwardVec.z + strafe * rightVec.z
//           = -strafe*sin - forward*cos
//
// Sanity yaw=0,    W only:  (0, -1)  ✓ forward = -Z
// Sanity yaw=π/2,  W only:  (-1, 0)  ✓ -X (yaw positive turns LEFT)
// Sanity yaw=0,    D only:  (+1, 0)  ✓ +X
// Sanity yaw=π/2,  D only:  (0, -1)  ✓ -Z (right of -X-forward)
// Sanity yaw=π/4,  W+D:     hypot==1 (NO cancellation)
export function computeVelocity(
  keys: ReadonlySet<string>,
  yaw: number,
  speed: number,
): { dx: number; dz: number } {
  const forward = (keys.has('w') ? 1 : 0) - (keys.has('s') ? 1 : 0);
  const strafe = (keys.has('d') ? 1 : 0) - (keys.has('a') ? 1 : 0);
  if (forward === 0 && strafe === 0) return { dx: 0, dz: 0 };
  // Normalize so diagonal isn't 1.41× faster than cardinal.
  const mag = Math.hypot(forward, strafe);
  const ndForward = forward / mag;
  const ndStrafe = strafe / mag;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  const worldDx = ndStrafe * cos - ndForward * sin;
  const worldDz = -ndStrafe * sin - ndForward * cos;
  return { dx: worldDx * speed, dz: worldDz * speed };
}

// Semi-implicit Euler: vY -= G*dt; y += vY*dt. Clamp at floor + reset vY
// on touch-down so subsequent gravity ticks don't accumulate underflow.
export function applyGravity(
  y: number,
  vY: number,
  dt: number,
  floorY: number,
): { y: number; vY: number; onFloor: boolean } {
  const newVY = vY - GRAVITY * dt;
  const newY = y + newVY * dt;
  if (newY <= floorY) {
    return { y: floorY, vY: 0, onFloor: true };
  }
  return { y: newY, vY: newVY, onFloor: false };
}

export function clampPitch(pitch: number): number {
  return Math.max(-PITCH_MAX, Math.min(PITCH_MAX, pitch));
}

// Three.js right-handed: forward = -Z at yaw=0, pitch=0.
// Sanity yaw=0,   pitch=0:    (0, 0, -1) — forward = -Z
// Sanity yaw=π/2, pitch=0:    (-1, 0, 0) — yaw positive rotates view LEFT toward -X
// Sanity yaw=0,   pitch=π/2:  (0, 1, 0)  — pitch positive looks UP
export function forwardVector(yaw: number, pitch: number): [number, number, number] {
  const cosP = Math.cos(pitch);
  const x = -Math.sin(yaw) * cosP;
  const y = Math.sin(pitch);
  const z = -Math.cos(yaw) * cosP;
  return [x, y, z];
}
